/* eslint-disable indent */
/* eslint-disable prettier/prettier */
/* eslint-disable react-native/no-inline-styles */
import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Animated, ActivityIndicator, StyleSheet, ScrollView } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { scale, is24HourFormat } from '@components/frame';
import { formatSinceTime, isWeb, Popup } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import { font } from '@styles/fonts';
import Button from '@styles/buttons';
import ColorWrapper from '@styles/colors';
import Line from '@components/common/line';
import InviteUserModal from './inviteUser';
import { getBoxShadowStyle } from '@styles/shadow';

import { searchUsers, searchInvitedUsers, inviteUser } from '@reducers/membership';
import Header from '@components/settings/v2/common/SettingsHeader';
import { BACK_BUTTON_SETTINGS_TEXT, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { buildSettingsNavParams } from '@components/settings/v2/common/navigationParams';

const InviteBtn = ({ inviteUser, user, user: { userProfile } }) => {
  const [loading, setLoading] = useState(false);
  return (
    <View style={{ marginRight: scale(-6) }}>
      <Button
        {...{
          variant: 'S',
          type: 'Secondary',
          loading,
          onPress: async () => {
            setLoading(true);
            await inviteUser({
              emailAddress: user.emailAddress,
              role: user.role,
              userProfile: {
                firstName: userProfile.firstName,
                lastName: userProfile.lastName,
              },
            });
            setLoading(false);
          },
        }}
      >
        {i18n.t('Re-Invite')}
      </Button>
    </View>
  );
};

const filters = {
  status: {
    value: 'Registered',
    valueType: 'string',
  },
};

const Users = ({ C, searchUsers, searchInvitedUsers, users = [], invitedUsers = [], loaded, isUsersMore, inviteUser, navigation, rootStack = 'Settings' }) => {
  const [alowLoadMore, setLoadMore] = useState(true);

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scrollRef = useRef(null);

  const toggleInviteUser = () => {
    Popup({
      Component: InviteUserModal,
    });
  };

  useEffect(() => {
    (async () => {
      await searchInvitedUsers({
        filters: {
          status: {
            value: 'Invited',
            valueType: 'string',
          },
        },
        orderBy: {
          invitedOn: 'Desc',
        },
        reset: true,
        size: 2,
      });

      await searchUsers({
        reset: true,
        filters,
      });
    })();
  }, []);

  const usersFormatted = users.map((u) => {
    const { userProfile = {}, role, lastUse } = u;
    return {
      name: `${userProfile.firstName} ${userProfile.lastName}`,
      bottom: role,
      value: lastUse ? formatSinceTime(lastUse, is24HourFormat) : '',
      onClick: () => {
        navigation.navigate(rootStack, buildSettingsNavParams(rootStack, 'Users', 'UserDetails', { id: userProfile.userId }));
      },
    };
  });
  const invitedUsersFormatted = invitedUsers
    .map((u) => {
      const { userProfile = {}, role, emailAddress } = u;
      return {
        name: `${userProfile.firstName} ${userProfile.lastName}`,
        value: () => <InviteBtn {...{ user: u, inviteUser, C }} />,
        bottom: role,
        onClick: () => {
          navigation.navigate(rootStack, buildSettingsNavParams(rootStack, 'Users', 'UserDetails', { id: userProfile.userId }));
        },
        valueFunction: true,
        noArrow: true,
      };
    })
    .slice(0, 2);

  const HeaderAction = () => (
    <View style={{ position: 'absolute', right: scale(10), top: scale(isWeb ? 9 : 24), zIndex: 100000 }}>
      <Button {...{ variant: 'S', type: 'Primary', onPress: toggleInviteUser }}>{i18n.t('Invite')}</Button>
    </View>
  );

  const isCloseToBottom = ({ layoutMeasurement, contentOffset, contentSize }) => {
    return layoutMeasurement.height + contentOffset.y >= contentSize.height - 1500;
  };

  const scrollListener = ({ nativeEvent }) => {
    if (alowLoadMore && isCloseToBottom(nativeEvent) && isUsersMore) {
      setLoadMore(false);
      searchUsers({
        callback: () => setLoadMore(true),
        filters,
      });
    }
  };

  return [
    <View
      key="UsersList"
      style={{
        width: '100%',
        backgroundColor: C('Background/Light/Detail'),
        height: '100%',
      }}
    >
      <Header name={'Users'} action={toggleInviteUser} backBtnText={BACK_BUTTON_SETTINGS_TEXT} />
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View
          style={{
            paddingHorizontal: scale(18),
          }}
        >
          {!!invitedUsersFormatted.length && (
            <View style={{ marginBottom: scale(27) }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingLeft: scale(15),
                }}
              >
                <Text
                  style={[
                    {
                      color: C('Label/Light/Tertiary'),
                    },
                    font['Footnote'],
                  ]}
                >
                  {i18n.t('INVITATION PENDING')}
                </Text>
              </View>
              <View style={{ width: '100%' }}>
                <View style={[shadowStyles, { marginTop: scale(5), width: '100%', borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
                  {invitedUsersFormatted.map((i, index) => (
                    <Line
                      {...{
                        item: i,
                        index,
                        array: invitedUsersFormatted,
                        bigSize: true,
                        C,
                        topBorder: index !== 0,
                        roundTop: index === 0,
                        roundBottom: false,
                      }}
                    />
                  ))}
                  <TouchableElement
                    onPress={() => {
                      navigation.navigate(rootStack, buildSettingsNavParams(rootStack, 'ReInvite'));
                    }}
                  >
                    {({ boxStyle, pressHoverColor }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            width: '100%',
                            backgroundColor: C(pressHoverColor('Background/Light/Field')),
                            height: scale(42),
                            paddingLeft: scale(15),
                            borderBottomLeftRadius: scale(10),
                            borderBottomRightRadius: scale(10),
                          },
                        ]}
                      >
                        <View
                          style={{
                            width: '100%',
                            borderTopWidth: StyleSheet.hairlineWidth,
                            borderTopColor: C('Hairline/Light/No trans'),
                            flex: 1,
                            justifyContent: 'center',
                          }}
                        >
                          <Text style={[font['Body'], { color: C('Link/Light/Primary') }]}>{i18n.t('Show all')}</Text>
                        </View>
                      </View>
                    )}
                  </TouchableElement>
                </View>
              </View>
            </View>
          )}
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Text
              style={[
                {
                  color: C('Label/Light/Tertiary'),
                  paddingLeft: scale(15),
                },
                font['Footnote'],
              ]}
            >
              {i18n.t('ACTIVE USERS')}
            </Text>
          </View>
          <View style={{ width: '100%' }}>
            <View style={[shadowStyles, { marginTop: scale(5), width: '100%', borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              {usersFormatted.map((i, index) => (
                <Line
                  {...{
                    item: i,
                    index,
                    array: usersFormatted,
                    bigSize: true,
                    C,
                    topBorder: index !== 0,
                    roundTop: index === 0,
                    roundBottom: index === usersFormatted.length - 1,
                  }}
                />
              ))}
            </View>
          </View>
          {!loaded || !alowLoadMore ? (
            <View style={{ width: '100%', marginTop: scale(20), justifyContent: 'center', height: 50 }}>
              <ActivityIndicator size="small" color="#777777" />
            </View>
          ) : null}
        </View>
      </ScrollView>
    </View>,
  ];
};

export default connect(({ membership: { users, loaded, isUsersMore, invitedUsers } }) => ({ users, loaded, isUsersMore, invitedUsers }), {
  searchUsers,
  searchInvitedUsers,
  inviteUser,
})(ColorWrapper(Users));
